// Is this a safe moment to reload the app silently onto a newer deploy?
// Fail CLOSED: every condition must hold, and any surprise means "not now".
// A "no" is never final — the caller simply asks again at its next chance.

import { isForeignDialogOpen } from '../../hooks/ui/useForeignDialogOpen';
import { hasUnsavedWork } from './unsavedWorkRegistry';
import { hasPendingWrites, msSinceLastWriteSettled } from './pendingWrites';
import { readIdleClock } from './idleClockSnapshot';
import { hasLiveTypedField, isEditableElement } from './editableTypingTracker';

export const WRITE_SETTLE_MS = 1500;
export const IDLE_SAFETY_MARGIN_MS = 60_000;
const ARIA_MODAL_SELECTOR = '[aria-modal="true"]';

const SIGNED_IN_PREFIXES = ['/app', '/platform'];

export function isSignedInAreaPath(pathname) {
  return SIGNED_IN_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function ariaModalOpen(doc) {
  const nodes = doc.querySelectorAll(ARIA_MODAL_SELECTOR);
  for (let i = 0; i < nodes.length; i += 1) {
    if (nodes[i].getClientRects().length > 0) return true;
  }
  return false;
}

function idleClockAllows() {
  const clock = readIdleClock();
  if (!clock) return false;
  if (clock.enabled === false) return true;
  const remaining = typeof clock.getRemainingMs === 'function' ? clock.getRemainingMs() : null;
  if (typeof remaining !== 'number' || Number.isNaN(remaining)) return false;
  const lead = Number(clock.leadMs);
  if (!Number.isFinite(lead)) return false;
  return remaining > lead + IDLE_SAFETY_MARGIN_MS;
}

export function canReloadSilently({
  now = Date.now(),
  doc = typeof document !== 'undefined' ? document : null,
  loc = typeof window !== 'undefined' ? window.location : null,
  nav = typeof navigator !== 'undefined' ? navigator : null,
} = {}) {
  try {
    if (!doc || !loc) return { ok: false, reason: 'error' };
    if (!isSignedInAreaPath(String(loc.pathname || ''))) return { ok: false, reason: 'path' };
    if (doc.visibilityState !== 'visible') return { ok: false, reason: 'hidden' };
    if (nav && nav.onLine === false) return { ok: false, reason: 'offline' };
    if (isForeignDialogOpen(doc) || ariaModalOpen(doc)) return { ok: false, reason: 'dialog' };
    if (isEditableElement(doc.activeElement)) return { ok: false, reason: 'focused-field' };
    if (hasUnsavedWork()) return { ok: false, reason: 'unsaved' };
    if (hasLiveTypedField()) return { ok: false, reason: 'typed' };
    if (hasPendingWrites() || msSinceLastWriteSettled(now) < WRITE_SETTLE_MS) {
      return { ok: false, reason: 'writes' };
    }
    if (!idleClockAllows()) return { ok: false, reason: 'idle' };
    return { ok: true, reason: 'ok' };
  } catch (_) {
    return { ok: false, reason: 'error' };
  }
}
